import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilter,
  faSearch,
  faChevronUp,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons";
import { getConfidenceDescription } from "../utils/confidenceUtils";
import EmailAnalysisAPI from "../../../services/EmailAnalysisAPI";
import DateRangeField from "../../../../common/datetime/DateRangeField";

const SearchFilters = ({
  userUuid,
  onAnalysesUpdate,
  onLoadingChange,
  onError,
}) => {
  const [searchCriteria, setSearchCriteria] = useState({
    isScam: undefined,
    minConfidence: 0,
    dateFrom: "",
    dateTo: "",
    sender: "",
    subject: "",
  });
  const [showSearchFilters, setShowSearchFilters] = useState(false);

  // Debug logging for component lifecycle
  useEffect(() => {
    console.log("SearchFilters: Component mounted with userUuid:", userUuid);
    return () => {
      console.log("SearchFilters: Component will unmount");
    };
  }, []);

  useEffect(() => {
    console.log("SearchFilters: userUuid changed to:", userUuid);
  }, [userUuid]);

  useEffect(() => {
    console.log("SearchFilters: searchCriteria changed to:", searchCriteria);
  }, [searchCriteria]);

  const toggleSearchFilters = () => {
    setShowSearchFilters(!showSearchFilters);
  };

  const handleSearch = async () => {
    if (!userUuid) {
      onError("User not authenticated. Please log in to search analyses.");
      return;
    }

    try {
      onLoadingChange(true);
      onError(null);

      const backendCriteria = {
        isScam: searchCriteria.isScam,
        minConfidence: searchCriteria.minConfidence,
        startDate: searchCriteria.dateFrom || null,
        endDate: searchCriteria.dateTo || null,
        sender: searchCriteria.sender || null,
        subject: searchCriteria.subject || null,
        userUuid: userUuid,
      };

      const filteredAnalyses =
        await EmailAnalysisAPI.searchAnalyses(backendCriteria);
      onAnalysesUpdate(filteredAnalyses);
      // Note: Search parameters are intentionally preserved after search completion
    } catch (err) {
      console.error("SearchFilters: Error searching analyses:", err);

      // Check if this is a "too many results" error
      if (
        err.response &&
        err.response.status === 400 &&
        err.response.data &&
        err.response.data.message &&
        err.response.data.message.includes("Too many results found")
      ) {
        onError(err.response.data.message);
      } else {
        onError("Failed to search analyses. Please try again.");
      }
    } finally {
      onLoadingChange(false);
    }
  };

  const handleClear = () => {
    // Reset all search criteria to default values
    setSearchCriteria({
      isScam: undefined,
      minConfidence: 0,
      dateFrom: "",
      dateTo: "",
      sender: "",
      subject: "",
    });
    // Note: Don't automatically search after clearing - let user manually trigger search
    // This preserves the previous search results until user clicks Search button
  };

  return (
    <>
      {/* Mobile Dropdown Filters */}
      <div className="common-sidebar-mobile">
        <div className="common-dropdown-container">
          <button
            className="common-dropdown-trigger"
            onClick={toggleSearchFilters}
            aria-expanded={showSearchFilters}
          >
            <div className="common-dropdown-current">
              <FontAwesomeIcon
                icon={faFilter}
                className="fa-icon-margin-right-8"
              />
              <span>Search Filters</span>
            </div>
            <FontAwesomeIcon
              icon={showSearchFilters ? faChevronUp : faChevronDown}
              className="common-dropdown-arrow"
            />
          </button>
          {showSearchFilters && (
            <div className="common-dropdown-content">
              <SearchForm
                searchCriteria={searchCriteria}
                setSearchCriteria={setSearchCriteria}
                onSearch={handleSearch}
                onClear={handleClear}
                isMobile={true}
              />
            </div>
          )}
        </div>
      </div>

      {/* Desktop Sidebar Filters */}
      <div className="common-sidebar common-sidebar-desktop">
        <div className="common-form-group">
          <h4>
            <FontAwesomeIcon
              icon={faFilter}
              className="fa-icon-margin-right-8"
            />
            Search Filters
          </h4>
          <SearchForm
            searchCriteria={searchCriteria}
            setSearchCriteria={setSearchCriteria}
            onSearch={handleSearch}
            onClear={handleClear}
            isMobile={false}
          />
        </div>
      </div>
    </>
  );
};

const SearchForm = ({
  searchCriteria,
  setSearchCriteria,
  onSearch,
  onClear,
  isMobile,
}) => {
  const idSuffix = isMobile ? "Mobile" : "";

  return (
    <>
      <div className="common-form-row">
        <div className="common-form-field">
          <label htmlFor={`scamStatus${idSuffix}`}>Scam Status</label>
          <select
            id={`scamStatus${idSuffix}`}
            value={searchCriteria.isScam ?? ""}
            onChange={(e) =>
              setSearchCriteria({
                ...searchCriteria,
                isScam:
                  e.target.value === "" ? undefined : e.target.value === "true",
              })
            }
          >
            <option value="">All Emails</option>
            <option value="true">Scam Detected</option>
            <option value="false">Legitimate</option>
          </select>
        </div>
      </div>

      <div className="common-form-row">
        <div className="common-form-field">
          <label htmlFor={`minConfidence${idSuffix}`}>
            {isMobile
              ? "Minimum Likelihood of Scam"
              : "Minimum Scam Likelihood"}
            : {getConfidenceDescription(searchCriteria.minConfidence)}
          </label>
          <input
            id={`minConfidence${idSuffix}`}
            type="range"
            min="0"
            max="1"
            step="0.125"
            value={searchCriteria.minConfidence}
            onChange={(e) =>
              setSearchCriteria({
                ...searchCriteria,
                minConfidence: parseFloat(e.target.value),
              })
            }
          />
        </div>
      </div>

      <div className="common-form-row">
        <div className="common-form-field">
          <DateRangeField
            legend="Date range"
            fromLabel="Date from"
            toLabel="Date to"
            from={searchCriteria.dateFrom}
            to={searchCriteria.dateTo}
            hint={
              isMobile
                ? undefined
                : "Optional — leave empty to search the full history."
            }
            onChange={({ from, to }) =>
              setSearchCriteria({
                ...searchCriteria,
                dateFrom: from,
                dateTo: to,
              })
            }
          />
        </div>
      </div>

      <div className="common-form-row">
        <div className="common-form-field">
          <label htmlFor={`sender${idSuffix}`}>Sender Email</label>
          <input
            id={`sender${idSuffix}`}
            type="text"
            placeholder="Filter by sender..."
            value={searchCriteria.sender}
            onChange={(e) =>
              setSearchCriteria({
                ...searchCriteria,
                sender: e.target.value,
              })
            }
          />
        </div>
      </div>

      <div className="common-form-row">
        <div className="common-form-field">
          <label htmlFor={`subject${idSuffix}`}>Email Subject</label>
          <input
            id={`subject${idSuffix}`}
            type="text"
            placeholder="Filter by subject..."
            value={searchCriteria.subject}
            onChange={(e) =>
              setSearchCriteria({
                ...searchCriteria,
                subject: e.target.value,
              })
            }
          />
        </div>
      </div>

      <div className="common-button-group">
        <button onClick={onSearch} className="common-button primary">
          <FontAwesomeIcon icon={faSearch} className="fa-icon-margin-right-8" />
          Search
        </button>
        <button
          onClick={onClear}
          className="common-button clear-button-secondary"
        >
          Clear
        </button>
      </div>
    </>
  );
};

export default SearchFilters;
